import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import SafeIcon from '../common/SafeIcon';
import { usePocketBaseRealtime } from '../hooks/usePocketBaseRealtime';
import { dbAdapter } from '../services/dbAdapter';
import ActiveTablesOverview from '../components/ActiveTablesOverview';
import TableOrderDrawer from '../components/TableOrderDrawer';
import QuickTablePickerModal from '../components/QuickTablePickerModal';

const { FiDollarSign, FiPrinter, FiCheck, FiClock, FiHash, FiAlertTriangle, FiShoppingBag, FiChevronDown } = FiIcons;

const Cassa = () => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [isClosingTable, setIsClosingTable] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Ascolto Realtime SSE nativo senza ricaricare la pagina
  const { data: comandeRealtime = [], loading } = usePocketBaseRealtime('comande');

  // Filtra solo le comande non ancora pagate
  const activeComande = useMemo(() => {
    return comandeRealtime.filter(c => c.status !== 'pagata');
  }, [comandeRealtime]);

  // Lista numeri tavoli attivi
  const activeTableNumbers = useMemo(() => {
    return [...new Set(activeComande.map(c => c.tavolo))];
  }, [activeComande]);

  // Comande del tavolo selezionato
  const comandeTavolo = useMemo(() => {
    if (!selectedTable) return [];
    return activeComande.filter(c => c.tavolo === selectedTable);
  }, [activeComande, selectedTable]);

  // Calcolo totale conto tavolo selezionato (TUTTI i piatti ordinati)
  const { totalAmount, hasPendingInKitchen } = useMemo(() => {
    let total = 0;
    let pending = false;

    comandeTavolo.forEach(c => {
      const dishes = Array.isArray(c.piatti) ? c.piatti : [];
      dishes.forEach(p => {
        const qty = Number(p.quantita || p.quantity || 1);
        const price = Number(p.prezzo || 0);
        total += (price * qty);
      });

      if (!c.consegnata && c.status !== 'completata') {
        pending = true;
      }
    });

    return { totalAmount: total, hasPendingInKitchen: pending };
  }, [comandeTavolo]);

  const handleSelectTable = (tableNum) => {
    setSelectedTable(tableNum);
    setIsDrawerOpen(true);
  };

  const handleTableClose = async () => {
    if (!selectedTable || comandeTavolo.length === 0) return;

    let warningMsg = `Confermi di chiudere il tavolo ${selectedTable}?`;
    if (hasPendingInKitchen) {
      warningMsg += `\nATTENZIONE: Questo tavolo ha portate ancora in preparazione in cucina!`;
    }
    warningMsg += `\nTotale: €${totalAmount.toFixed(2)}`;

    if (!confirm(warningMsg)) return;

    setIsClosingTable(true);
    try {
      const comandeIds = comandeTavolo.map(comanda => comanda.id);
      await dbAdapter.closeTavolo(selectedTable, comandeIds);
      
      setIsDrawerOpen(false);
      setSelectedTable(null);
      alert(`Tavolo ${selectedTable} chiuso con successo! Incasso registrato.`);
    } catch (error) {
      console.error('Errore chiusura tavolo:', error);
      alert('Errore durante la chiusura del tavolo');
    } finally {
      setIsClosingTable(false);
    }
  };

  const handlePrintReceipt = () => {
    if (!selectedTable || comandeTavolo.length === 0) return;

    setIsPrinting(true);
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text(`Ricevuta - Tavolo ${selectedTable}`, 105, 15, { align: 'center' });
      
      // Data
      doc.setFontSize(10);
      const today = format(new Date(), 'dd/MM/yyyy HH:mm', { locale: it });
      doc.text(`Data: ${today}`, 105, 25, { align: 'center' });
      
      // Dati Ristorante
      doc.text('Tap2Order Restaurant', 105, 30, { align: 'center' });
      doc.text('Via Roma 123, Milano', 105, 35, { align: 'center' });
      doc.text('P.IVA: 12345678901', 105, 40, { align: 'center' });
      
      // Tabella Piatti
      const tableColumn = ['Piatto', 'Quantità', 'Prezzo Unit.', 'Totale'];
      const tableRows = [];

      comandeTavolo.forEach(comanda => {
        const dishes = Array.isArray(comanda.piatti) ? comanda.piatti : [];
        dishes.forEach(piatto => {
          const qty = Number(piatto.quantita || piatto.quantity || 1);
          const price = Number(piatto.prezzo || 0);
          const subtotal = price * qty;
          tableRows.push([
            piatto.nome,
            qty,
            `€${price.toFixed(2)}`,
            `€${subtotal.toFixed(2)}`
          ]);
        });
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 50,
        theme: 'grid',
        headStyles: { fillColor: [92, 113, 94] },
        styles: { fontSize: 8 },
        margin: { top: 50 }
      });

      // Totale Finale
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Totale: €${totalAmount.toFixed(2)}`, 150, finalY, { align: 'right' });

      // Note di chiusura
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Grazie per averci scelto!', 105, finalY + 15, { align: 'center' });

      // Download PDF
      doc.save(`Ricevuta_Tavolo${selectedTable}_${today.replace(/[/:]/g, '-')}.pdf`);
    } catch (error) {
      console.error('Errore generazione ricevuta:', error);
      alert('Errore durante la generazione della ricevuta');
    } finally {
      setIsPrinting(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'HH:mm', { locale: it });
    } catch {
      return '--:--';
    }
  };

  if (loading && comandeRealtime.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C715E]"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-4 py-8">
      {/* Intestazione Cassa */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center justify-center">
          <SafeIcon icon={FiDollarSign} className="w-10 h-10 mr-3 text-[#5C715E]" />
          Gestione Cassa & Tavoli
        </h1>
        <p className="text-base text-gray-600">
          Monitoraggio conti in tempo reale, stampa ricevute e chiusura cassa
        </p>
      </div>

      {/* PANORAMICA TAVOLI ATTIVI IN PRIMO PIANO */}
      <ActiveTablesOverview
        comande={activeComande}
        selectedTable={selectedTable}
        onSelectTable={handleSelectTable}
        onOpenNewTable={() => setShowTablePicker(true)}
        mode="cassa"
      />

      {/* DETTAGLIO TAVOLO SELEZIONATO IN PAGINA (PER SCHERMI GRANDI) */}
      {selectedTable && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mt-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b border-gray-100 gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <span className="text-3xl font-black text-gray-900">Tavolo {selectedTable}</span>
                {hasPendingInKitchen ? (
                  <span className="px-3 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 mr-1 text-orange-600" />
                    Portate in preparazione in cucina
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full flex items-center">
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 mr-1" />
                    Tutte le portate servite
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {comandeTavolo.length} comande &bull; Conto totale calcolato in tempo reale
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrintReceipt}
                disabled={isPrinting || comandeTavolo.length === 0}
                className="flex items-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiPrinter} className="w-4 h-4 mr-2" />
                {isPrinting ? 'Stampa...' : 'Stampa Preconto'}
              </button>

              <button
                onClick={handleTableClose}
                disabled={isClosingTable || comandeTavolo.length === 0}
                className="flex items-center px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-colors shadow-lg disabled:opacity-50"
              >
                <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                {isClosingTable ? 'Chiusura...' : 'Chiudi Tavolo (Pagato)'}
              </button>
            </div>
          </div>

          {/* Griglia Comande del Tavolo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            {comandeTavolo.map((comanda, idx) => {
              const dishes = Array.isArray(comanda.piatti) ? comanda.piatti : [];

              return (
                <div key={comanda.id || idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                    <span className="font-bold text-sm text-gray-800">
                      Comanda #{comanda.codice_comanda || comanda.id?.slice(0, 4)}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400 flex items-center">
                        <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                        {formatTime(comanda.timestamp || comanda.created)}
                      </span>
                      {comanda.consegnata ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                          Servito
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
                          In cucina
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    {dishes.map((p, pIdx) => {
                      const qty = Number(p.quantita || p.quantity || 1);
                      const price = Number(p.prezzo || 0);
                      return (
                        <div key={pIdx} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            <span className="font-bold text-[#5C715E] mr-1.5">{qty}x</span>
                            {p.nome}
                          </span>
                          <span className="font-medium text-gray-900">
                            €{(price * qty).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totale in Basso */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <span className="text-base font-semibold text-gray-600">Totale Conto Tavolo:</span>
            <span className="text-3xl font-black text-emerald-700">€{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* DRAWER SPLIT-VIEW A COMPARSA RAPIDA */}
      <TableOrderDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tableNumber={selectedTable}
        comande={activeComande}
        onPrintReceipt={handlePrintReceipt}
        onCloseTable={handleTableClose}
        isProcessing={isClosingTable || isPrinting}
        mode="cassa"
      />

      {/* MODAL NUMPAD SELETTORE RAPIDO TAVOLO */}
      <QuickTablePickerModal
        isOpen={showTablePicker}
        onClose={() => setShowTablePicker(false)}
        onSelectTable={handleSelectTable}
        activeTables={activeTableNumbers}
      />
    </motion.div>
  );
};

export default Cassa;